/**
 * The home's line drawings — one per task kind, one per activity kind, and the two response marks.
 *
 * ── Most of them are the app's own, not this screen's ────────────────────────────────────────
 * A clock, a target, a note, a ruler, an envelope, a tick and a chevron all already ship, in
 * `shared/ui/EmptyStateIcons` (transcribed from the mobile app's glyph registry) and
 * `shared/ui/AuthIcons`. This file drew a second version of each at its own stroke weight, so the
 * same concept had two drawings and a trainer met a different clock on this screen than on the
 * client detail. They are imported now, at the shared set's own weight — `docs/design/README.md`'s
 * rule is one drawing per concept, and where the two disagreed the shipped one wins.
 *
 * What stays here is what the mock draws and the app does not have: a crossed-out calendar, an
 * hourglass, a speech bubble, a trophy, a flag, the session chips' gauge and two arrows, and the
 * clip frame's play and fullscreen marks. Each is traced from the owner's approved mock at its own
 * stroke weight (2.2) on a 24-unit box, the same way `NavRailIcons` and `CalendarEventIcons` are
 * traced from their canons. The response marks and the feed's chevron are not here at all — they
 * were one-line forwarders onto `ElapsedMarkIcon`, `CheckIcon` and `ChevronRightIcon`, and their
 * surfaces import those directly.
 *
 * Every one of them is DECORATIVE: the tile around a task glyph carries the reason's name, and the
 * activity row spells out what happened in words, so announcing the drawing as well would be noise.
 *
 * `currentColor` throughout, because the tint is the surface's decision and arrives through the
 * stylesheet — `ReasonTile` inks a task's kind, `toneTile.module.css` an activity's.
 */

import { MailIcon, WarningIcon } from '~/shared/ui/AuthIcons';
import { GoalMarkIcon } from '~/shared/ui/EmptyStateIcons';

import type { TaskGlyph } from '../model/taskAction';
import type { SignalId } from '../model/sessionVerdict';

export interface GlyphProps {
  readonly className?: string;
}

/** The shape the mock's own glyphs share. Stroke 2.2 on a 24 box, round joins, no fill. */
function Stroke({ className, d }: GlyphProps & { readonly d: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d={d} />
    </svg>
  );
}

/** A missed session: the calendar with the day struck out. */
function CalendarXMark({ className }: GlyphProps) {
  return <Stroke className={className} d="M4 6h16v14H4zM8 3v4M16 3v4M4 10h16M10 14l4 4M14 14l-4 4" />;
}

/** A plan running out, and an invitation still waiting. */
function HourglassMark({ className }: GlyphProps) {
  return <Stroke className={className} d="M7 3h10M7 21h10M8 3v4l4 5 4-5V3M8 21v-4l4-5 4 5v4" />;
}

/** An unread message — and the card's „Napisz DM". */
export function ChatMark({ className }: GlyphProps) {
  return <Stroke className={className} d="M21 12a8 8 0 0 1-8 8H4l2-3a8 8 0 1 1 15-5z" />;
}

/** A client's recording: a frame with the play mark in it. */
function VideoMark({ className }: GlyphProps) {
  return (
    <Stroke
      className={className}
      d="M6 5h12a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V8a3 3 0 0 1 3-3zM10 9l5 3-5 3z"
    />
  );
}

/** A client's question under a recording: a film camera, the recording's own kind of drawing. */
function CameraMark({ className }: GlyphProps) {
  return <Stroke className={className} d="M5 7h9a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2zM16 10.5l5-3v9l-5-3" />;
}

/**
 * The acts a `/zadania` card offers that no drawing above already names (owner, 25 September 2026:
 * every act is a small line glyph beside its word). Same box and stroke as the rest of this file.
 */

/** „Otwórz klienta": a head and shoulders. */
export function PersonMark({ className }: GlyphProps) {
  return <Stroke className={className} d="M12 12a4 4 0 1 0 0-8a4 4 0 1 0 0 8zM4.5 20.5a7.5 7.5 0 0 1 15 0" />;
}

/** „Dodaj tydzień": the calendar with a plus on it. */
export function CalendarPlusMark({ className }: GlyphProps) {
  return <Stroke className={className} d="M20.5 11V7.5A2.5 2.5 0 0 0 18 5H6a2.5 2.5 0 0 0-2.5 2.5V18A2.5 2.5 0 0 0 6 20.5h6M3.5 9.5h17M8 3v4M16 3v4M18 15v6M15 18h6" />;
}

/** „Wyślij ponownie": a paper plane. */
export function PaperPlaneMark({ className }: GlyphProps) {
  return <Stroke className={className} d="M21 3 10.5 13.5M21 3l-6.5 18-4-7.5L3 9.5z" />;
}

/** „Kopiuj wiadomość": two sheets, one over the other. */
export function CopyMark({ className }: GlyphProps) {
  return <Stroke className={className} d="M9 9h10a1.5 1.5 0 0 1 1.5 1.5v9A1.5 1.5 0 0 1 19 21H9a1.5 1.5 0 0 1-1.5-1.5v-9A1.5 1.5 0 0 1 9 9zM4.5 15V5A1.5 1.5 0 0 1 6 3.5h10" />;
}

/**
 * The five drawings KANON "Karty zadań" (24 September 2026) gives the kinds of task that had none of
 * their own: a finished session is a dumbbell, a survey a form, an empty calendar a blank one, a
 * half-done session a half circle, and a self-paced plan that closed itself a pulse. Traced from the
 * mock's `cards.js` on the same 24 box; the plan kinds take the flag above.
 */
export function DumbbellMark({ className }: GlyphProps) {
  return <Stroke className={className} d="M7 12h10M4 8.5h3v7H4zM17 8.5h3v7h-3zM2 10.5v3M22 10.5v3" />;
}

export function FormMark({ className }: GlyphProps) {
  return <Stroke className={className} d="M7 3.5h10a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-13a2 2 0 0 1 2-2zM9 8.5h6M9 12h6M9 15.5h3.5" />;
}

/** An empty calendar — and „Kalendarz klienta", the way to the client's own. */
export function CalendarEmptyMark({ className }: GlyphProps) {
  return <Stroke className={className} d="M6 5h12a2.5 2.5 0 0 1 2.5 2.5v10.5a2.5 2.5 0 0 1-2.5 2.5H6a2.5 2.5 0 0 1-2.5-2.5V7.5A2.5 2.5 0 0 1 6 5zM3.5 9.5h17M8 3v4M16 3v4" />;
}

function HalfMark({ className }: GlyphProps) {
  return <Stroke className={className} d="M12 3.5a8.5 8.5 0 1 0 0 17a8.5 8.5 0 1 0 0-17zM12 3.5v17" />;
}

function PulseMark({ className }: GlyphProps) {
  return <Stroke className={className} d="M3 12h4l2-5 4 10 2-5h6" />;
}

/** A personal record — and, on the Pulpit's activity feed, a win. */
export function TrophyMark({ className }: GlyphProps) {
  return (
    <Stroke
      className={className}
      d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0zM7 6H4a3 3 0 0 0 3 3M17 6h3a3 3 0 0 1-3 3"
    />
  );
}

/** A goal reached. Drawn only by the Pulpit's activity feed; no queue row uses it. */
export function FlagMark({ className }: GlyphProps) {
  return <Stroke className={className} d="M5 21V4M5 4h11l-2 4 2 4H5" />;
}

/** The night before a session — the task card's sleep chip. The mock's moon. */
export function SleepMark({ className }: GlyphProps) {
  return <Stroke className={className} d="M19 14.5A7.5 7.5 0 0 1 9.5 5a7.5 7.5 0 1 0 9.5 9.5z" />;
}

/** How the client felt, on the 1–10 scale the contract fixes. A dial with its needle. */
function GaugeMark({ className }: GlyphProps) {
  return <Stroke className={className} d="M4.4 17.5a8.6 8.6 0 1 1 15.2 0M12 17.5l3.6-4.6" />;
}

/** A set whose load fell short of its own prescription. */
function ArrowDownMark({ className }: GlyphProps) {
  return <Stroke className={className} d="M12 5v14M19 12l-7 7-7-7" />;
}

/** A set whose load went past it. The exact mirror, because the two counts are the same unit. */
function ArrowUpMark({ className }: GlyphProps) {
  return <Stroke className={className} d="M12 19V5M5 12l7-7 7 7" />;
}

/**
 * A clip's play control, and the one FILLED mark on this screen — everything else here is a 2.2
 * stroke because everything else is a sign, and a hollow play triangle would be the only one a
 * trainer meets all day.
 *
 * It stands on EVERY closed clip frame, over the poster and over a frame that has none, so the two
 * differ by a picture rather than by a symbol.
 */
export function PlayMark({ className }: GlyphProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
      <path d="M6 4l14 8-14 8z" />
    </svg>
  );
}

const SIGNAL_MARKS: Readonly<Record<SignalId, (props: GlyphProps) => React.ReactElement>> = {
  mood: GaugeMark,
  skipped: WarningIcon,
  weightDown: ArrowDownMark,
  weightUp: ArrowUpMark,
  record: TrophyMark,
};

const TASK_MARKS: Readonly<Record<TaskGlyph, (props: GlyphProps) => React.ReactElement>> = {
  dumbbell: DumbbellMark,
  form: FormMark,
  calendarEmpty: CalendarEmptyMark,
  half: HalfMark,
  pulse: PulseMark,
  flag: FlagMark,
  calendarX: CalendarXMark,
  hourglass: HourglassMark,
  chat: ChatMark,
  mail: MailIcon,
  target: GoalMarkIcon,
  video: VideoMark,
  camera: CameraMark,
  // The session chips' own moon and dial, and the activity feed's trophy: one drawing per concept.
  sleep: SleepMark,
  gauge: GaugeMark,
  trophy: TrophyMark,
};

export function SignalGlyphMark({ id, className }: GlyphProps & { readonly id: SignalId }) {
  const Mark = SIGNAL_MARKS[id];
  return <Mark className={className} />;
}

export function TaskGlyphMark({ glyph, className }: GlyphProps & { readonly glyph: TaskGlyph }) {
  const Mark = TASK_MARKS[glyph];
  return <Mark className={className} />;
}
